import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { ALL_CASES } from "@/data/caseStudies";
import { ARTICLES, articleHref } from "@/data/blogArticles";
import { SERVICES } from "@/data/services";
import { FadeUp, Eyebrow } from "@/lib/motion";

const MAIN = [
  { to: "/ai-visibility", label: "SEO + AI Visibility Offer", note: "Founding rate — $397/mo" },
  { to: "/", label: "Home", note: "Marketing that moves leads and calls" },
  { to: "/packages", label: "Packages", note: "All core offers at a glance" },
  { to: "/free-strategy-call", label: "Free Strategy Call", note: "Book 30 minutes with Todd" },
  { to: "/contact", label: "Contact", note: "Todd answers every message personally" },
  { to: "/privacy", label: "Privacy Policy", note: "How we handle your data" },
];

const COMPANY = [
  { to: "/about", label: "About", note: "The studio since 2006" },
  { to: "/todd", label: "Todd Meisler", note: "Founder & Chief Strategist" },
  { to: "/faq", label: "FAQ", note: "29 straight answers" },
  { to: "/testimonials", label: "Client Testimonials", note: "Every client word, lightly polished" },
  { to: "/case-studies", label: "Case Studies", note: "Real clients. Real turnarounds." },
  { to: "/blog", label: "Blog", note: "Resources & guides" },
  { to: "/cross-web-repair", label: "Cross-Web Repair Program", note: "Fix the gaps everywhere" },
];

const slugId = (to) => to.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function Group({ title, links, testid }) {
  return (
    <FadeUp>
      <h2 className="mt-14 font-display text-2xl font-extrabold tracking-tight text-slate-900 first:mt-0 sm:text-3xl">
        {title}
      </h2>
      <ul className="mt-6 grid gap-3 sm:grid-cols-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              data-testid={`${testid}-${slugId(l.to)}`}
              className="group flex h-full items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/40"
            >
              <span>
                <span className="block font-semibold text-slate-900">{l.label}</span>
                {l.note && (
                  <span className="mt-0.5 block text-base leading-snug text-faint">{l.note}</span>
                )}
              </span>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-flame transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </li>
        ))}
      </ul>
    </FadeUp>
  );
}

export default function SitemapPage() {
  const caseLinks = ALL_CASES.map((c) => ({
    to: `/case-studies/${c.slug}`,
    label: c.client,
    note: c.industry,
  }));
  const blogLinks = ARTICLES.map((a) => ({
    to: articleHref(a),
    label: a.title,
    note: a.category,
  }));
  const serviceLinks = SERVICES.map((s) => ({
    to: s.route || `/${s.slug}`,
    label: s.title,
  }));

  return (
    <main data-testid="sitemap-page">
      <Seo
        title="Sitemap — Every Page on the ZD Design Site"
        description="HTML sitemap for ZD Design: services, case studies, resources & guides, and company pages — everything on the site in one place."
        path="/sitemap"
      />

      <section className="grain relative overflow-hidden pb-14 pt-40 md:pb-16 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Sitemap</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              Every page,{" "}
              <span className="text-flame">one place.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              Services, case studies, resources &amp; guides and company pages —
              for people and search engines alike.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <Group title="Services" links={serviceLinks} testid="sitemap-service" />
          <Group title="Case studies" links={caseLinks} testid="sitemap-case" />
          <Group title="Resources & guides" links={blogLinks} testid="sitemap-blog" />
          <Group title="Company" links={COMPANY} testid="sitemap-company" />
          <Group title="Get started" links={MAIN} testid="sitemap-main" />
        </div>
      </section>
    </main>
  );
}
